import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { authAPI } from '../services/api';
import { clearLoginNudgeSessionKeys } from '../lib/loginTipNudge';
import { clearNativeToken, isNative, isNativeAndroid, isNativeIOS, setNativeToken } from '../lib/nativeBridge';
import { appleNativeSignIn } from '../lib/nativeApp';

const AUTH_CACHE_KEY = 'fs_auth_cache_v1';

const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID || '';
const APPLE_NATIVE_CLIENT_ID =
  process.env.REACT_APP_APPLE_NATIVE_CLIENT_ID || 'com.stagster.app';

const loadAppleSDK = () => {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.AppleID) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src*="appleid.auth.js"]');
    if (existing) return resolve();
    const script = document.createElement('script');
    script.src =
      'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Could not load Sign in with Apple'));
    document.head.appendChild(script);
  });
};

const generateNonce = () => {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

const AuthContext = createContext({});

const readCachedUser = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(AUTH_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
};

const writeCachedUser = (user) => {
  if (typeof window === 'undefined') return;
  try {
    if (user) {
      window.sessionStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(user));
    } else {
      window.sessionStorage.removeItem(AUTH_CACHE_KEY);
    }
  } catch (_) {
    // no-op
  }
};

const augmentUserFromEdgeSession = (payload) => {
  const u = payload?.session?.user;
  if (!u?.id) return null;
  return {
    id: u.id,
    email: u.email || null,
    full_name: u.full_name || u.name || null,
    provider: u.provider || 'google',
    profile_image_url: u.profile_image_url || u.picture || null,
    role: u.role || 'user',
    // Fail closed: a session with no invite_status is treated as pending,
    // never as accepted. The backend always sends the field on /auth/session.
    invite_status: u.invite_status || 'pending',
    invite_required: Boolean(u.invite_required),
    access_token: null,
    streak_info: u.streak_info || null,
  };
};

const unsupportedAuthMethod = async () => ({
  data: null,
  error: new Error('This auth method is not supported in STAGSTER V2. Use Google sign-in.'),
});

const scrubAuthUrlState = () => {
  if (typeof window === 'undefined') return;

  if (window.location.hash && window.location.hash.includes('access_token')) {
    window.history.replaceState(null, '', window.location.pathname);
  }

  try {
    const url = new URL(window.location.href);
    if (url.pathname === '/auth-callback') return;

    const removable = ['code', 'state', 'error', 'error_description', 'access_token'];
    let mutated = false;
    removable.forEach((param) => {
      if (url.searchParams.has(param)) {
        url.searchParams.delete(param);
        mutated = true;
      }
    });

    if (mutated) {
      const cleaned =
        url.pathname +
        (url.searchParams.toString() ? `?${url.searchParams.toString()}` : '') +
        (url.hash || '');
      window.history.replaceState(null, '', cleaned);
    }
  } catch (err) {
    console.warn('AuthContext: failed to scrub auth params', err);
  }
};

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => readCachedUser());
  const [loading, setLoading] = useState(() => !readCachedUser());

  useEffect(() => {
    writeCachedUser(user);
  }, [user]);

  const refreshSession = useCallback(async () => {
    try {
      const edgeSession = await authAPI.getSession();
      const edgeUser = augmentUserFromEdgeSession(edgeSession);
      setUser(edgeUser);
      return edgeUser;
    } catch (error) {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    scrubAuthUrlState();
    refreshSession();

    const handleFocus = () => {
      refreshSession();
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [refreshSession]);

  const signOut = async () => {
    try {
      await authAPI.logout();
    } catch (_) {
      // best effort
    }

    // Native shell: drop the stored session JWT (Sprint 1). No-op on web.
    try {
      await clearNativeToken();
    } catch (_) {
      // best effort
    }

    setUser(null);
    setLoading(false);

    try {
      window.sessionStorage.removeItem(AUTH_CACHE_KEY);
    } catch (_) {
      // no-op
    }
    try {
      clearLoginNudgeSessionKeys();
    } catch (_) {
      // no-op
    }

    return { error: null };
  };

  const buildAuthRedirect = () => {
    if (typeof window === 'undefined') return undefined;
    try {
      const origin = window.location.origin;
      const url = new URL(window.location.href);

      ['code', 'state', 'next', 'error', 'error_description', 'access_token'].forEach((param) => {
        if (url.searchParams.has(param)) {
          url.searchParams.delete(param);
        }
      });

      let nextPath = url.pathname + (url.searchParams.toString() ? `?${url.searchParams.toString()}` : '');
      if (!nextPath || nextPath.startsWith('/auth-callback')) {
        nextPath = '/';
      }

      const callbackUrl = new URL(`${origin}/auth-callback`);
      callbackUrl.searchParams.set('next', nextPath);
      return callbackUrl.toString();
    } catch (_) {
      return `${window.location.origin}/auth-callback`;
    }
  };

  const signInWithGoogle = async () => {
    try {
      const redirectTo = buildAuthRedirect();
      const nextPath = redirectTo ? new URL(redirectTo).searchParams.get('next') || '/' : '/';
      authAPI.startGoogleAuth({ next: nextPath });
      return { data: { redirected: true }, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const signInWithGoogleCredential = async () => signInWithGoogle();

  /** Sign in with Apple (guideline 4.8): verify the identity token
   *  server-side, then sync the session. Native shell uses the system
   *  ASAuthorization flow (bundle-id audience); web uses the Apple JS SDK
   *  popup (Services-ID audience). Hidden when no client id is configured. */
  const signInWithApple = async () => {
    if (isNativeAndroid()) {
      // No Apple SDK on Android; Apple sign-in is iOS/web only. Google is the
      // Android path (Apple's guideline 4.8 applies to the iOS app, which has it).
      return { data: null, error: new Error('Sign in with Apple is not available on Android — use Google') };
    }
    if (!APPLE_CLIENT_ID && !isNativeIOS()) {
      return { data: null, error: new Error('Apple sign-in is not configured') };
    }
    try {
      const nonce = generateNonce();
      const state = generateNonce();
      let identityToken = '';
      let user = null;
      if (isNativeIOS()) {
        const res = await appleNativeSignIn({ clientId: APPLE_NATIVE_CLIENT_ID, nonce });
        identityToken = res.identityToken;
        user = res.user;
      } else {
        await loadAppleSDK();
        window.AppleID.auth.init({
          clientId: APPLE_CLIENT_ID,
          scope: 'name email',
          redirectURI: window.location.origin,
          usePopup: true,
          nonce,
          state,
        });
        const response = await window.AppleID.auth.signIn();
        identityToken = response?.authorization?.id_token;
        user = response?.user || null;
      }
      if (!identityToken) {
        throw new Error('Missing Apple identity token');
      }
      const result = await authAPI.appleAuth(identityToken, nonce, user);
      if (result?.token && isNative()) {
        await setNativeToken(result.token);
      }
      const edgeUser = await refreshSession();
      return { data: { user: edgeUser }, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    signUp: unsupportedAuthMethod,
    signIn: unsupportedAuthMethod,
    signOut,
    signInWithGoogle,
    signInWithGoogleCredential,
    signInWithApple,
    signInWithLinkedIn: unsupportedAuthMethod,
    signInWithPhone: unsupportedAuthMethod,
    verifyPhoneOTP: unsupportedAuthMethod,
    signInWithEmail: unsupportedAuthMethod,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
